import { isToday, isYesterday, isThisWeek, format, parseISO } from 'date-fns';
import type {
  DateGroup,
  BaseEventTimelineItem,
  GroupedTimelineItem,
} from '../types/eventTimeline';

/**
 * Categorize a timestamp into a date group
 * @param timestamp - ISO 8601 date string
 * @returns Date group category
 */
export function getDateGroup(timestamp: string): DateGroup {
  const date = parseISO(timestamp);

  if (isToday(date)) {
    return 'Today';
  }

  if (isYesterday(date)) {
    return 'Yesterday';
  }

  // Check if this week (Monday-Sunday)
  if (isThisWeek(date, { weekStartsOn: 1 })) {
    return 'This Week';
  }

  return 'Older';
}

/**
 * Group timeline events by date category
 * Maintains chronological order (most recent first)
 *
 * @param events - Array of timeline events
 * @returns Events grouped by date category
 */
export function groupTimelineByDate<T extends BaseEventTimelineItem>(
  events: T[]
): GroupedTimelineItem<T>[] {
  // Group events by date category
  const groupMap = new Map<DateGroup, T[]>();

  events.forEach(event => {
    const group = getDateGroup(event.timestamp);
    if (!groupMap.has(group)) {
      groupMap.set(group, [event]);
    } else {
      groupMap.get(group)!.push(event);
    }
  });

  // Convert to array and sort by date group order
  const groupOrder: DateGroup[] = ['Today', 'Yesterday', 'This Week', 'Older'];

  return groupOrder
    .filter(group => groupMap.has(group))
    .map(group => ({
      dateGroup: group,
      events: groupMap.get(group)!,
    }));
}

/**
 * Format event time as HH:mm (e.g., "14:30")
 * @param timestamp - ISO 8601 date string
 * @returns Formatted time string
 */
export function formatEventTime(timestamp: string): string {
  const date = parseISO(timestamp);
  return format(date, 'HH:mm');
}

/**
 * Format event date as "dd MMM yyyy" (e.g., "15 Jan 2024")
 * @param timestamp - ISO 8601 date string
 * @returns Formatted date string
 */
export function formatEventDate(timestamp: string): string {
  const date = parseISO(timestamp);
  return format(date, 'dd MMM yyyy');
}
